/**
 * Component for LanguageTransformModal
 */

//  Generic imports
import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";

//  Motif imports
import MotifModal, {
  MotifModalHeader,
  MotifModalBody,
  MotifModalFooter,
} from "@ey-xd/motif-react/Modal";
import MotifButton from "@ey-xd/motif-react/Button";
import MotifCheckbox from "@ey-xd/motif-react/Checkbox";
import { MotifSelect, MotifOption, MotifLabel } from "@ey-xd/motif-react";
import MotifFormField from "@ey-xd/motif-react/FormField";

//  Internal imports
import "./LanguageTransformModal.scss";
import LanguageService from "../../../../services/languageService";

const LanguageTransform = ({
  openModal,
  setOpenModal,
  languageTransform,
  setLanguageTransform,
  handleTransformLanguage,
  clearAllSelections,
}) => {

  const [languages, setLanguages] = useState([]);

  const languageService = new LanguageService();

  const config = {
    checkboxes: [
      { name: "improveLang", label: "Improve written language" },
      { name: "concise", label: "Make more concise" },
      { name: "translate", label: "Translate" },
    ],
    dropdowns: [
      { name: "sourceLang", label: "From", visibleOptions: 3 },
      { name: "targetLang", label: "To", visibleOptions: 3 },
    ],
  };
  
  /**
   * Function to get MicroSoft Languages
   */
  useEffect(() => {
    getLanguagesAsync();
    //  eslint-disable-next-line
  }, []);

  /**
   * Function to get all MicroSoft Languages
   */
  const getLanguagesAsync = async () => {
    let vMSLanguages = await languageService.getLanguages();
    if (vMSLanguages) {
      vMSLanguages = JSON.parse(vMSLanguages);
      if (vMSLanguages?.translation) {
        const vTranslations = vMSLanguages.translation;
        const vTranslationKeys = Object.keys(vTranslations);
        if (vTranslationKeys.length > 0) {
          let vLanguages = [];
          for (const element of vTranslationKeys) {
            vLanguages.push({
              code: element,
              name: vTranslations[element]['name'],
              nativeName: vTranslations[element]['nativeName']
            })
          }
          setLanguages(vLanguages);
        }
      }
    } else {
      setLanguages([]);
    }
  };
  
  const handleCheckboxChange = (e) => {
    const { name, checked } = e.target;
    setLanguageTransform((prev) => {
      const updatedCheckboxes = {
        ...prev.checkboxes,
        [name]: checked,
      };

      const updatedDropdownValues =
        name === "translate" && !checked
          ? clearDropdownValues()
          : prev.dropdownValues;

      return {
        ...prev,
        checkboxes: updatedCheckboxes,
        dropdownValues: updatedDropdownValues,
      };
    });
  };

  const handleDropdownChange = (name, value) => {
    setLanguageTransform((prev) => ({
      ...prev,
      dropdownValues: {
        ...prev.dropdownValues,
        [name]: value,
      },
    }));
  };

  const clearDropdownValues = () =>
    config.dropdowns.reduce((acc, dropdown) => {
      acc[dropdown.name] = "";
      return acc;
    }, {});

  const areAllDropdownsSelected = () =>
    config.dropdowns.every((dropdown) =>
      languageTransform.dropdownValues[dropdown.name]?.trim()
    );

  /**
   * Check if selected unique languages
   * @returns 
   */
  const areUniqueLanguages = () => {
    if (config.dropdowns.length > 0) {
      const firstElementValue = languageTransform.dropdownValues[config.dropdowns[0].name];
      const secondElementValue = languageTransform.dropdownValues[config.dropdowns[1].name];
      return firstElementValue !== secondElementValue;
    }
  }

  const isConfirmButtonEnabled = () => {
    const isCheckboxSelected = Object.values(languageTransform.checkboxes).some(
      (checked) => checked
    );

    if (!isCheckboxSelected) {
      return false;
    }

    if (languageTransform.checkboxes.translate) {
      if (!areAllDropdownsSelected()) {
        return false;
      }
      if (!areUniqueLanguages()) {
        return false;
      }
    }

    return true;
  };

  return (
    <MotifModal
      show={openModal}
      onClose={() => {
        setOpenModal(!openModal);
        clearAllSelections();
      }}
      className="languageTransformModal genericModal modalMotifFooterModal"
      size="xl"
      focusTrapOptions={{
        tabbableOptions: {
          displayCheck: "none",
        },
      }}
    >
      <MotifModalHeader
        closeButtonProps={{ "aria-label": "Close", title: "Close" }}
        className="langTransformHeader"
      >
        <div className="langTransformHead">Language Options</div>
      </MotifModalHeader>
      <MotifModalBody className="languageTransformModalBody">
        {config.checkboxes.map((checkbox) => (
          <div key={checkbox.name} className="checkbox-container">
            <MotifCheckbox
              id={checkbox.name}
              name={checkbox.name}
              onChange={handleCheckboxChange}
              checked={languageTransform.checkboxes[checkbox.name]}
            >
              <span className="checkboxLabel">{checkbox.label}</span>
            </MotifCheckbox>
          </div>
        ))}

        {languageTransform.checkboxes.translate && (
          <div className="dropdown-container">
            {config.dropdowns.map((dropdown) => (
              <MotifFormField key={dropdown.name} className="form-field">
                <MotifLabel id={`${dropdown.name}-label`}>
                  {dropdown.label}
                </MotifLabel>
                <MotifSelect
                  className="selectInput"
                  ariaLabelledBy={`${dropdown.name}-label`}
                  onChange={(value) =>
                    handleDropdownChange(dropdown.name, value)
                  }
                  searchPlaceholder="Select language"
                  filter={true}
                  value={languageTransform.dropdownValues[dropdown.name]}
                  visibleOptions={dropdown.visibleOptions}
                >
                  {languages?.map((lang) => (
                    <MotifOption
                      key={lang.code}
                      value={lang.code}
                      className="languageName"
                    >
                      {lang.name}
                    </MotifOption>
                  ))}
                </MotifSelect>
              </MotifFormField>
            ))}
          </div>
        )}
      </MotifModalBody>
      <MotifModalFooter className="languageTransformFooter">
        <div className="languageTransformBtn">
          <MotifButton
            className="btnCancel"
            variant="secondary"
            onClick={() => {
              setOpenModal(false);
              clearAllSelections();
            }}
          >
            Cancel
          </MotifButton>
          <MotifButton
            className="btnConfirm"
            disabled={!isConfirmButtonEnabled()}
            onClick={handleTransformLanguage}
          >
            Confirm
          </MotifButton>
        </div>
      </MotifModalFooter>
    </MotifModal>
  );
};

LanguageTransform.propTypes = {
  openModal: PropTypes.bool.isRequired,
  setOpenModal: PropTypes.func.isRequired,
  languageTransform: PropTypes.shape({
    checkboxes: PropTypes.objectOf(PropTypes.bool).isRequired,
    dropdownValues: PropTypes.objectOf(PropTypes.string).isRequired,
  }).isRequired,
  setLanguageTransform: PropTypes.func.isRequired,
  handleTransformLanguage: PropTypes.func.isRequired,
  clearAllSelections: PropTypes.func,
};

export default LanguageTransform;
